import { Component, forwardRef, input, InputSignal, signal, WritableSignal } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CardSelectorOption } from './card-selector.interface';
import { FormattedCurrencyPipe } from '../../../pipes/formatted-currency.pipe';
import { replacePreTags } from '../../../utils/text.utils';

/**
 * Generic card selector component with ControlValueAccessor
 * Can be used with reactive forms to select from a list of card options
 *
 * @example
 * <app-card-selector
 *   formControlName="selectedOption"
 *   [options]="cardOptions"
 *   [currencyMinorUnit]="0"
 *   title="Select an Option"
 *   titleIcon="icon-delivery-outline"
 * />
 */
@Component({
  selector: 'app-card-selector',
  imports: [FormattedCurrencyPipe],
  templateUrl: './card-selector.component.html',
  styleUrl: './card-selector.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CardSelectorComponent),
      multi: true
    }
  ]
})
export class CardSelectorComponent<T = any> implements ControlValueAccessor {
  /** List of options to display */
  options: InputSignal<CardSelectorOption<T>[]> = input<
    CardSelectorOption<T>[]
  >([]);

  /** Currency minor unit for price formatting (default: 0) */
  currencyMinorUnit: InputSignal<number> = input<number>(0);

  /** Optional title to display above the cards */
  title: InputSignal<string | undefined> = input<string | undefined>(undefined);

  /** Optional icon to display next to the title */
  titleIcon: InputSignal<string | undefined> = input<string | undefined>(
    undefined
  );

  /** Show price in cards (default: true) */
  showPrice: InputSignal<boolean> = input<boolean>(true);

  /** Show icon in cards (default: true) */
  showIcon: InputSignal<boolean> = input<boolean>(true);

  /** Show description in cards (default: true) */
  showDescription: InputSignal<boolean> = input<boolean>(true);

  selectedOption: WritableSignal<CardSelectorOption<T> | null> = signal(null);
  disabled: WritableSignal<boolean> = signal(false);

  private onChange: (value: CardSelectorOption<T> | null) => void = () => {};
  private onTouched: () => void = () => {};

  selectOption(option: CardSelectorOption<T>): void {
    if (this.disabled() || option.disabled) {
      return;
    }

    this.selectedOption.set(option);
    this.onChange(option);
    this.onTouched();
  }

  writeValue(value: CardSelectorOption<T> | null): void {
    this.selectedOption.set(value);
  }

  registerOnChange(fn: (value: CardSelectorOption<T> | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  isSelected(option: CardSelectorOption<T>): boolean {
    return this.selectedOption()?.id === option.id;
  }

  processDescription(description: string | undefined): string {
    if (!description) {
      return '';
    }
    return replacePreTags(description);
  }
}
